import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import type { ThemePreference } from '@neuroedge/shared';
import { useUpdateProfile } from '../api/auth';
import { useAuth } from '../auth/AuthContext';

const STORAGE_KEY = 'ne-theme-mode';

/**
 * Applies `mode` to the DOM (`<html data-theme>`, tokens.css reacts to it)
 * and remembers it in localStorage purely so a page reload can repaint
 * instantly in the right theme before React/auth has even mounted (see the
 * inline script in index.html) - it is never the source of truth; the
 * signed-in user's `themePreference` (persisted server-side) is.
 */
function applyThemeAttribute(mode: ThemePreference): void {
  const root = document.documentElement;
  if (mode === 'AUTO') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode === 'DARK' ? 'dark' : 'light');
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // best-effort only - a blocked/unavailable localStorage just means no
    // instant-paint cache next load, not a broken theme this session.
  }
}

interface ThemeContextValue {
  /** The signed-in user's saved preference - 'DARK' pre-login/pre-load too, matching the DOM's default baseline (tokens.css). */
  mode: ThemePreference;
  /** Applies immediately and persists to the user's profile (PATCH /auth/me). No-op while signed out. */
  setMode: (mode: ThemePreference) => void;
  saving: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user, refreshMe } = useAuth();
  const updateProfile = useUpdateProfile();

  // Re-applies once per signed-in user id, not on every render, so a local
  // `setMode` call isn't immediately clobbered back by this effect while
  // the PATCH /auth/me + refreshMe round-trip is still in flight.
  const appliedForUserId = useRef<string | null>(null);
  useEffect(() => {
    if (!user) return;
    if (appliedForUserId.current === user.id) return;
    appliedForUserId.current = user.id;
    applyThemeAttribute(user.themePreference);
  }, [user]);

  const setMode = useCallback(
    (mode: ThemePreference) => {
      applyThemeAttribute(mode); // instant, optimistic - don't wait on the network for the repaint
      if (!user) return;
      void updateProfile.mutateAsync({ themePreference: mode }).then(() => refreshMe());
    },
    [user, updateProfile, refreshMe],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode: user?.themePreference ?? 'DARK',
      setMode,
      saving: updateProfile.isPending,
    }),
    [user, setMode, updateProfile.isPending],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
